import { useState, useCallback, useMemo } from "react";
import cloudApi from "../../../services/cloudApi";
import {
  extractPreviewImages,
  calculateEstimatedImageCount,
} from "../utils/imageDownloadUtils";

/**
 * Download phases
 */
export const DOWNLOAD_PHASES = {
  IDLE: "idle",
  PREPARING: "preparing",
  DOWNLOADING: "downloading",
  COMPLETE: "complete",
  ERROR: "error",
};

/**
 * Custom hook for managing image download functionality (backend-only)
 *
 * @param {Object} tableHeader - Table metadata with ID
 * @param {Array} columns - All column definitions
 * @param {Array} currentPageData - Currently loaded row data for preview
 * @param {number} totalRows - Total row count from pagination
 * @returns {Object} Image download state and functions
 */
export const useImageDownload = (tableHeader, columns, currentPageData, totalRows) => {
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Download state
  const [downloadState, setDownloadState] = useState({
    isDownloading: false,
    phase: DOWNLOAD_PHASES.IDLE,
    error: null,
  });

  // Get image columns from all columns
  const imageColumns = useMemo(() => {
    if (!columns) return [];
    return columns.filter((col) => col.type === "image");
  }, [columns]);

  // Check if download is available
  const canDownload = useMemo(() => {
    return imageColumns.length > 0 && tableHeader?.id;
  }, [imageColumns, tableHeader]);

  // Open modal
  const openModal = useCallback(() => {
    setIsModalOpen(true);
    // Reset download state when opening
    setDownloadState({
      isDownloading: false,
      phase: DOWNLOAD_PHASES.IDLE,
      error: null,
    });
  }, []);

  // Close modal
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  /**
   * Main download function - always uses backend
   * @param {Array} selectedColumns - Selected image columns
   * @param {string} namingTemplate - Filename template
   */
  const handleDownload = useCallback(
    async (selectedColumns, namingTemplate) => {
      if (!tableHeader?.id || selectedColumns.length === 0) {
        setDownloadState((prev) => ({
          ...prev,
          error: "No table or columns selected",
        }));
        return;
      }

      setDownloadState({
        isDownloading: true,
        phase: DOWNLOAD_PHASES.PREPARING,
        error: null,
      });

      try {
        // Small delay to show the preparing state
        await new Promise((r) => setTimeout(r, 300));

        setDownloadState((prev) => ({
          ...prev,
          phase: DOWNLOAD_PHASES.DOWNLOADING,
        }));

        // Trigger backend download - this opens a new tab/downloads directly
        const selectedColumnIds = selectedColumns.map((c) => c.id);
        await cloudApi.downloadImagesZip(tableHeader.id, selectedColumnIds, namingTemplate);

        setDownloadState({
          isDownloading: false,
          phase: DOWNLOAD_PHASES.COMPLETE,
          error: null,
        });

        // Auto-close modal after successful download trigger
        setTimeout(() => {
          setIsModalOpen(false);
        }, 1500);
      } catch (error) {
        setDownloadState({
          isDownloading: false,
          phase: DOWNLOAD_PHASES.ERROR,
          error: error.message,
        });
      }
    },
    [tableHeader]
  );

  /**
   * Get preview images for modal
   */
  const getPreviewImages = useCallback(
    (selectedColumns, maxCount = 6) => {
      return extractPreviewImages(currentPageData, selectedColumns, maxCount);
    },
    [currentPageData]
  );

  /**
   * Get estimated image count
   */
  const getEstimatedCount = useCallback(
    (selectedColumns) => {
      return calculateEstimatedImageCount(currentPageData, selectedColumns, totalRows);
    },
    [currentPageData, totalRows]
  );

  return {
    // Modal state
    isModalOpen,
    openModal,
    closeModal,

    // Image columns
    imageColumns,
    canDownload,

    // Download state
    downloadState,

    // Download function
    handleDownload,

    // Preview helpers
    getPreviewImages,
    getEstimatedCount,
  };
};
